import { RoutePaths } from '@shared/config/routePathsEnum';
import { appStore } from '@shared/model/app_store/AppStore';

import { CHAT_POPUP_ACTIVE_STORAGE_KEY, CHAT_POPUP_FROM_MAIN_FETCH_ONCE_SESSION_KEY, OPERATOR_CHAT_POPUP_WINDOW_NAME } from './constants';
import { markElectronPopupOpenRestGeneration } from './electronPopupUnreadRest';
import { estimateOperatorChatPopupOuterSize } from './estimateOperatorChatPopupWindowSize';
import {
  clearMainRestoreIsChatOpenFromPopup,
  readOperatorPopupPreviewSnapshot,
} from './mainChatOpenRestoreFromPopup';
import { writeOperatorChatPopupFrameLock } from './operatorChatPopupFrameLock';
import { resolveBottomRightPopupScreenPosition } from './operatorChatPopupLayout';
import { clearOperatorChatPopupLayoutStorage } from './popupLayoutStorage';

/**
 * Отдельное окно с тем же origin (сессия из storage).
 * `popup=yes` — в Chromium открывается не вкладка, а всплывающее окно с урезанным UI (см. window.open features).
 * Размер задаётся здесь; в popup только восстановление lock при сбое ОС (без подгонки по dock).
 * Для Electron: токен передаётся через URL параметр.
 */
export async function openOperatorChatPopup(): Promise<void> {
  clearOperatorChatPopupLayoutStorage();
  clearMainRestoreIsChatOpenFromPopup();
  try {
    sessionStorage.removeItem(CHAT_POPUP_FROM_MAIN_FETCH_ONCE_SESSION_KEY);
  } catch {
    /* ignore */
  }
  
  const desktopBridge = window.alcolockDesktop;
  let tokenParam = '';
  let authToken: string | null = null;

  // Для Electron: JWT из основного окна (cookie bearer) → URL + IPC payload
  if (desktopBridge) {
    try {
      authToken = await desktopBridge.getAuthToken();
      if (authToken) {
        tokenParam = `?token=${encodeURIComponent(authToken)}`;
      }
    } catch {
      /* ignore */
    }
  }
  
  const url = `${window.location.origin}${RoutePaths.operatorChatPopup}${tokenParam}`;
  const hasPreviewSnapshot = readOperatorPopupPreviewSnapshot().length > 0;
  const { outerW: estOuterW, outerH: estOuterH } = estimateOperatorChatPopupOuterSize({
    includePreviewColumn: Boolean(desktopBridge) || hasPreviewSnapshot,
  });
  let outerW = Math.min(window.screen.availWidth, estOuterW);
  let outerH = Math.min(window.screen.availHeight, estOuterH);
  outerW = Math.max(estOuterW, outerW);
  outerH = Math.max(estOuterH, outerH);

  const scr = window.screen as Screen & { availLeft?: number; availTop?: number };
  const desktopPosition = resolveBottomRightPopupScreenPosition(outerW, outerH);
  const left = desktopBridge
    ? desktopPosition.left
    : scr.availLeft! + Math.max(0, (scr.availWidth - outerW) / 2);
  const top = desktopBridge
    ? desktopPosition.top
    : scr.availTop! + Math.max(0, (scr.availHeight - outerH) / 2);

  const features = [
    'popup=yes',
    `width=${Math.round(outerW)}`,
    `height=${Math.round(outerH)}`,
    `left=${Math.round(left)}`,
    `top=${Math.round(top)}`,
    'menubar=no',
    'toolbar=no',
    'location=no',
    'status=no',
    'scrollbars=no',
    /* resizable=yes: иначе Chrome даёт сжать окно вручную, но блокирует resizeTo для отката. */
    'resizable=yes',
  ].join(',');

  writeOperatorChatPopupFrameLock({
    outerW: Math.round(outerW),
    outerH: Math.round(outerH),
    left: Math.round(left),
    top: Math.round(top),
  });

  if (desktopBridge) {
    const selectedBranch = appStore.getState().selectedBranchState;
    const branchState =
      selectedBranch?.id != null
        ? { id: selectedBranch.id, name: selectedBranch.name || '' }
        : undefined;

    try {
      localStorage.setItem(CHAT_POPUP_ACTIVE_STORAGE_KEY, String(Date.now()));
      markElectronPopupOpenRestGeneration();
    } catch {
      /* ignore */
    }
    void desktopBridge.openOperatorChatPopup({
      url,
      authToken: authToken || undefined,
      branchState,
      lock: {
        outerW: Math.round(outerW),
        outerH: Math.round(outerH),
        left: Math.round(left),
        top: Math.round(top),
      },
    });
    return;
  }

  const win = window.open(url, OPERATOR_CHAT_POPUP_WINDOW_NAME, features);
  win?.focus();
}

/** Снимает подавление чата в основной вкладке, фокусирует её и закрывает окно popup. */
export function closeOperatorChatPopupAndRestoreMain(): void {
  try {
    localStorage.removeItem(CHAT_POPUP_ACTIVE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    window.opener?.focus();
  } catch {
    /* ignore */
  }
  if (window.alcolockDesktop) {
    void window.alcolockDesktop.closeCurrentWindow();
    return;
  }
  window.close();
}
